/**
 * Typography utility functions for building Tailwind classes
 */

import type {
	FontStyle,
	FontWeight,
	TextColor,
	ITypographyProps,
} from './types';
import { cn } from '../../lib/utils';

/**
 * Maps text color prop to Tailwind text color class
 */
const TEXT_COLOR_MAP: Record<TextColor, string> = {
	primary: 'text-text-primary',
	secondary: 'text-text-secondary',
	tertiary: 'text-text-tertiary',
	white: 'text-text-white',
	black: 'text-text-black',
	link: 'text-text-link',
	danger: 'text-text-danger',
	warning: 'text-text-warning',
	success: 'text-text-success',
	neutral: 'text-text-neutral',
	score1: 'text-text-score-1',
	score2: 'text-text-score-2',
	score3: 'text-text-score-3',
	score4: 'text-text-score-4',
	score5: 'text-text-score-5',
} as const;

/**
 * Maps font weight prop to Tailwind font weight class
 * Uses standard Tailwind utility class names (kebab-case)
 */
const FONT_WEIGHT_MAP: Record<FontWeight, string> = {
	thin: 'font-thin',
	extraLight: 'font-extralight',
	light: 'font-light',
	regular: 'font-normal',
	medium: 'font-medium',
	semiBold: 'font-semibold',
	bold: 'font-bold',
	extraBold: 'font-extrabold',
	black: 'font-black',
} as const;

/**
 * Gets the Tailwind text color class for a given color
 */
const getTextColorClass = (color: TextColor = 'primary'): string =>
	TEXT_COLOR_MAP[color];

/**
 * Gets the Tailwind font weight class for a given weight
 */
const getFontWeightClass = (fontWeight?: FontWeight): string =>
	fontWeight ? FONT_WEIGHT_MAP[fontWeight] : '';

/**
 * Gets the Tailwind font style class for a given style
 */
const getFontStyleClass = (fontStyle: FontStyle = 'normal'): string => {
	if (fontStyle === 'normal') return '';
	if (fontStyle === 'italic') return 'italic';
	if (fontStyle === 'oblique') return 'italic'; // Tailwind uses 'italic' class for oblique as well
	return '';
};

/**
 * Builds Tailwind classes for typography components
 *
 * @param baseClasses - Base typography classes (font size, line height)
 * @param props - Typography props
 * @returns Combined Tailwind classes
 */
export const buildTypographyClasses = (
	baseClasses: string,
	props: ITypographyProps
): string => {
	const {
		color = 'primary',
		hasEllipsis = false,
		fontStyle = 'normal',
		isUpperCase = false,
		fontWeight,
		className,
	} = props;

	return cn(
		'm-0', // Base: margin reset
		baseClasses,
		getTextColorClass(color),
		getFontWeightClass(fontWeight),
		getFontStyleClass(fontStyle),
		isUpperCase && 'uppercase',
		hasEllipsis && 'truncate', // truncate = overflow-hidden text-ellipsis whitespace-nowrap
		className
	);
};
